import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { container, density, radius, typography } from "@aeriosiq/ui";
import { UmbrellaIcon, WrenchIcon } from "@aeriosiq/ui/icons";

import {
  useTimeTrackingConfigQuery,
  usePutTimeTrackingConfigMutation,
} from "../hooks.js";
import type { TimeTrackingConfig } from "../types.js";
import { useTimeTrackingReviewAccess } from "./useTimeTrackingReviewAccess.js";

/**
 * TT-03 — the project's commercial config for standby. Mockup screen D.
 *
 * Two toggles and two rates: what may be charged to the END CUSTOMER, and at
 * what hourly price. Set once by the PM; drives the invoice print and the
 * review totals bar.
 *
 * Two things this card deliberately does NOT do:
 *
 *   - **Toggling a type off does not hide it from pilots.** Standby still gets
 *     tracked either way — the tracking is the evidence behind "not tracked in
 *     our system → we don't pay it". The toggle only decides whether it
 *     produces a customer-invoice amount.
 *   - **It does not clear the rate when a type is switched off.** A PM turning
 *     weather off for a month should not have to retype 95.00 when it comes
 *     back. The field is disabled, its value is kept, and the BE stores it.
 *
 * The phase-2 subcontractor frame-contract row is rendered disabled, per the
 * mockup: visible intent, no function.
 */
export interface TimeTrackingConfigCardProps {
  projectId: string;
}

interface Draft {
  weatherInvoiceable: boolean;
  technicalInvoiceable: boolean;
  weatherPrice: string;
  technicalPrice: string;
}

function toDraft(cfg: TimeTrackingConfig | undefined): Draft {
  return {
    weatherInvoiceable: cfg?.weatherInvoiceable ?? false,
    technicalInvoiceable: cfg?.technicalInvoiceable ?? false,
    weatherPrice:
      cfg?.weatherPricePerHour == null ? "" : String(cfg.weatherPricePerHour),
    technicalPrice:
      cfg?.technicalPricePerHour == null
        ? ""
        : String(cfg.technicalPricePerHour),
  };
}

/** `""` → null (not set); anything non-numeric or negative is rejected. */
export function parsePrice(value: string): number | null | "invalid" {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return "invalid";
  return n;
}

export function TimeTrackingConfigCard({
  projectId,
}: TimeTrackingConfigCardProps) {
  const { canWriteConfig } = useTimeTrackingReviewAccess();
  const configQuery = useTimeTrackingConfigQuery(projectId);
  const putMutation = usePutTimeTrackingConfigMutation(projectId);

  const [draft, setDraft] = useState<Draft>(() => toDraft(undefined));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the draft once the server answers, and re-seed after a save so the
  // card always shows what is stored rather than what was typed.
  useEffect(() => {
    if (configQuery.data) setDraft(toDraft(configQuery.data));
  }, [configQuery.data]);

  const currency = configQuery.data?.currency ?? "EUR";

  async function handleSave(): Promise<void> {
    setError(null);
    setSaved(false);
    const weather = parsePrice(draft.weatherPrice);
    const technical = parsePrice(draft.technicalPrice);
    if (weather === "invalid" || technical === "invalid") {
      setError("A rate must be a number of at least 0.");
      return;
    }
    try {
      await putMutation.mutateAsync({
        weatherInvoiceable: draft.weatherInvoiceable,
        technicalInvoiceable: draft.technicalInvoiceable,
        weatherPricePerHour: weather,
        technicalPricePerHour: technical,
        currency,
      });
      setSaved(true);
    } catch {
      setError("Could not save the configuration.");
    }
  }

  if (configQuery.isPending) {
    return (
      <Stack alignItems="center" sx={{ py: 3 }}>
        <CircularProgress aria-label="Loading time-tracking configuration" />
      </Stack>
    );
  }

  if (configQuery.isError) {
    return (
      <Alert severity="error">Could not load the standby configuration.</Alert>
    );
  }

  return (
    <Paper
      variant="outlined"
      // `prose` is the right ceiling for a two-row form: wide enough for the
      // label + toggle + rate triple, narrow enough that the rate does not
      // drift a screen away from the switch it belongs to.
      sx={{ p: 2.5, borderRadius: radius.card, maxWidth: container.prose }}
      data-testid="tt-config-card"
    >
      <Typography component="h2" sx={{ fontWeight: 600, mb: 0.5 }}>
        Standby &amp; time tracking
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What may be charged to the customer on this project, and at what rate.
        Switching a type off does not stop pilots reporting it — it only stops
        it producing an invoice amount.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {saved ? (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="tt-config-saved">
          Saved.
        </Alert>
      ) : null}

      <ConfigRow
        icon={<UmbrellaIcon fontSize="small" />}
        label="Weather standby"
        testIdPrefix="tt-config-weather"
        invoiceable={draft.weatherInvoiceable}
        price={draft.weatherPrice}
        currency={currency}
        disabled={!canWriteConfig}
        onToggle={(v) => {
          setDraft((d) => ({ ...d, weatherInvoiceable: v }));
        }}
        onPrice={(v) => {
          setDraft((d) => ({ ...d, weatherPrice: v }));
        }}
      />
      <ConfigRow
        icon={<WrenchIcon fontSize="small" />}
        label="Technical standby"
        testIdPrefix="tt-config-technical"
        invoiceable={draft.technicalInvoiceable}
        price={draft.technicalPrice}
        currency={currency}
        disabled={!canWriteConfig}
        onToggle={(v) => {
          setDraft((d) => ({ ...d, technicalInvoiceable: v }));
        }}
        onPrice={(v) => {
          setDraft((d) => ({ ...d, technicalPrice: v }));
        }}
      />

      {/* Phase 2, per mockup D — visible intent, no function. What the
          SUBCONTRACTOR charges US is a frame-contract concern; the MVP's
          "not tracked → not paid" rule works operationally without it. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ py: 1.5 }}
        data-testid="tt-config-phase2"
      >
        {/* NOT dimmed with opacity. A composited grey label measured 4.42:1
            and failed WCAG AA — "this is phase 2" is carried by the chip and
            by the absence of any control, not by making the words harder to
            read. */}
        <Typography variant="body2" color="text.secondary">
          Subcontractor frame-contract rates
        </Typography>
        <Chip label="phase 2" size="small" />
      </Stack>

      {canWriteConfig ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            variant="contained"
            disabled={putMutation.isPending}
            onClick={() => {
              void handleSave();
            }}
            data-testid="tt-config-save"
            sx={{ minHeight: density.comfortable.controlHeight }}
          >
            Save
          </Button>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          You do not have permission to change these rates.
        </Typography>
      )}
    </Paper>
  );
}

function ConfigRow({
  icon,
  label,
  testIdPrefix,
  invoiceable,
  price,
  currency,
  disabled,
  onToggle,
  onPrice,
}: {
  icon: React.ReactNode;
  label: string;
  testIdPrefix: string;
  invoiceable: boolean;
  price: string;
  currency: string;
  disabled: boolean;
  onToggle: (value: boolean) => void;
  onPrice: (value: string) => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ py: 1.5, borderBottom: 1, borderColor: "divider" }}
    >
      <Box sx={{ display: "inline-flex", color: "text.secondary" }} aria-hidden>
        {icon}
      </Box>
      <Typography variant="body2" sx={{ flex: 1 }}>
        <strong>{label}</strong> invoiceable to customer
      </Typography>
      <Switch
        checked={invoiceable}
        disabled={disabled}
        onChange={(e) => {
          onToggle(e.target.checked);
        }}
        // The accessible name IS the test handle here — the tests query by
        // role + name, which is what a screen reader does too.
        inputProps={{ "aria-label": `${label} invoiceable to customer` }}
        data-testid={`${testIdPrefix}-toggle`}
      />
      <TextField
        size="small"
        // Disabled, not cleared — see the component header.
        disabled={disabled || !invoiceable}
        value={price}
        onChange={(e) => {
          onPrice(e.target.value);
        }}
        label="Rate"
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: {
            inputMode: "decimal",
            "data-testid": `${testIdPrefix}-price`,
            "aria-label": `${label} rate per hour`,
          },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Typography sx={{ ...typography.label }}>
                  {currency}/h
                </Typography>
              </InputAdornment>
            ),
          },
        }}
        sx={{ width: density.comfortable.controlHeight * 3.5 }}
      />
    </Stack>
  );
}
